/**
 * Report service for API interactions
 * Based on interviewService but customized for reports
 */
import apiClient from './api';

class ReportService {
  constructor() {
    this.baseURL = '/reports'; // Fixed: removed '/api' since apiClient already has it
  }

  // Get all reports with filtering
  async getReports(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `${this.baseURL}?${queryString}` : this.baseURL;
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  }

  // Get report by ID
  async getReport(reportId) {
    try {
      const response = await apiClient.get(`${this.baseURL}/${reportId}`);
      return response;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  }

  // Create new report
  async createReport(reportData) {
    try {
      const response = await apiClient.post(this.baseURL, reportData);
      return response;
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  }

  // Upload file and create report
  async uploadFile(file, reportName, reportDescription, keyFindings = '', personName = '', jobDescription = '', companyName = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('report_name', reportName);
      formData.append('report_description', reportDescription);
      formData.append('key_findings', keyFindings);
      formData.append('person_name', personName);
      formData.append('job_description', jobDescription);
      formData.append('company_name', companyName);

      console.log('Sending upload request to:', `${this.baseURL}/upload`);
      console.log('FormData contents:', {
        file: file.name,
        report_name: reportName,
        report_description: reportDescription,
        key_findings: keyFindings,
        person_name: personName,
        job_description: jobDescription,
        company_name: companyName
      });

      const response = await apiClient.post(`${this.baseURL}/upload`, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Upload response received:', response);
      return response;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Scrape URL and create report
  async scrapeUrl(url, reportName, reportDescription, personName = '', jobDescription = '', companyName = '') {
    try {
      const formData = new FormData();
      formData.append('url', url);
      formData.append('report_name', reportName);
      formData.append('report_description', reportDescription);
      formData.append('person_name', personName);
      formData.append('job_description', jobDescription);
      formData.append('company_name', companyName);

      console.log('Sending URL scrape request to:', `${this.baseURL}/scrape-url`);
      
      const response = await apiClient.post(`${this.baseURL}/scrape-url`, formData, {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('URL scrape response received:', response);
      return response;
    } catch (error) {
      console.error('Error scraping URL:', error);
      throw error;
    }
  }

  // Update report
  async updateReport(reportId, updateData) {
    try {
      const response = await apiClient.put(`${this.baseURL}/${reportId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating report:', error);  
      throw error;
    }
  }

  // Delete report
  async deleteReport(reportId) {
    try {
      const response = await apiClient.delete(`${this.baseURL}/${reportId}`);
      return response;
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  // Extract content from uploaded file
  async extractContent(reportId) {
    try {
      const response = await apiClient.post(`${this.baseURL}/${reportId}/extract-content`);
      return response;
    } catch (error) {
      console.error('Error extracting content:', error);
      throw error;
    }
  }

  // Check for duplicates
  async checkDuplicate(reportId) {
    try {
      const response = await apiClient.post(`${this.baseURL}/${reportId}/duplicate-check`);
      return response;
    } catch (error) {
      console.error('Error checking duplicate:', error);
      throw error;
    }
  }

  // Scrape document content using Claude Sonnet 4.0
  async scrapeDocument(reportId) {
    try {
      const response = await apiClient.post(`${this.baseURL}/${reportId}/scrape`);
      return response;
    } catch (error) {
      console.error('Error scraping document:', error);
      throw error;
    }
  }

  // Generate article from report content
  async generateArticle(reportId) {
    try {
      const response = await apiClient.post(`${this.baseURL}/${reportId}/generate-article`, {}, {
        timeout: 300000  // 5 minutes to match KeyCrew processing time
      });
      return response;
    } catch (error) {
      console.error('Error generating article:', error);
      throw error;
    }
  }

  // Get report statistics
  async getStats() {
    try {
      const response = await apiClient.get(`${this.baseURL}/stats/overview`);
      return response;
    } catch (error) {
      console.error('Error fetching report stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
const reportService = new ReportService();
export default reportService;